'use client'
import type { FC } from 'react'
import { useEffect, useMemo } from 'react'
import React from 'react'
import styles from './AddressBookMobile.module.scss'
import { useErrorHandling } from '@/tools/o11y/client'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import type { UserAddress, UserAddressNew } from '@/user/address-book/entities'
import { floatingButtonVariants } from '@/user/address-book/steps'
import {
  mapHeight,
  sheetHeight,
} from '@/user/address-book/ui/components/address-book-mobile/constants/ui-arangement'
import { AddressMap } from '@/user/address-book/ui/components/address-map/AddressMap'
import { ContentSheet } from '@/user/address-book/ui/components/content-sheet/ContentSheet'
import { FloatingButton } from '@/user/address-book/ui/components/floating-button/FloatingButton'
import { AddressDetailsForm } from '@/user/address-book/ui/steps/address-details-form/AddressDetailsForm'
import { AddressTypeForm } from '@/user/address-book/ui/steps/address-type-form/AddressTypeForm'
import { FindAddressByText } from '@/user/address-book/ui/steps/find-address-by-text/FindAddressByText'
import { FindAddressOnMap } from '@/user/address-book/ui/steps/find-address-on-map/FindAddressOnMap'
import { SetEntrance } from '@/user/address-book/ui/steps/set-entrance/SetEntrance'
import { WhereToDeliver } from '@/user/address-book/ui/steps/where-to-deliver/WhereToDeliver'

interface Props {
  onClose: () => void
  onSelect: (address: UserAddress | UserAddressNew) => void
}

export const AddressBookMobile: FC<Props> = ({ onClose, onSelect }) => {
  const { execWithErrorTracking } = useErrorHandling()
  const { currentStep, setCloseAddressBook, setAddressChangeSuccess, setIsMobile } =
    useAddressBookContext()

  const stepStyles = useMemo(
    () => ({
      mapStyle: { bottom: `calc(${mapHeight[currentStep]} - 24px)` },
      sheetStyle: { top: `calc(100% - ${sheetHeight[currentStep]})` },
      isSheetFullscreen: sheetHeight[currentStep] === '100%',
    }),
    [currentStep]
  )

  useEffect(() => {
    execWithErrorTracking(() => {
      setCloseAddressBook(() => onClose)
      setAddressChangeSuccess(() => onSelect)
      setIsMobile(true)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onSelect])

  const floatingButtonVariant = floatingButtonVariants[currentStep]

  return (
    <div className={styles.addressFlow}>
      <AddressMap style={stepStyles.mapStyle} />
      {floatingButtonVariant && <FloatingButton variant={floatingButtonVariant} />}
      <ContentSheet isFullscreen={stepStyles.isSheetFullscreen} style={stepStyles.sheetStyle}>
        {currentStep === 'WHERE_TO_DELIVER' && <WhereToDeliver />}
        {currentStep === 'FIND_ADDRESS_ON_MAP' && <FindAddressOnMap />}
        {currentStep === 'FIND_ADDRESS_BY_TEXT' && <FindAddressByText />}
        {currentStep === 'ADDRESS_TYPE_FORM' && <AddressTypeForm />}
        {currentStep === 'ADDRESS_DETAILS_FORM' && <AddressDetailsForm />}
        {currentStep === 'SET_ENTRANCE' && <SetEntrance />}
      </ContentSheet>
    </div>
  )
}
